import nativeSpec from "./native-spec.json" with { type: "json" };
import appsSpec from "./apps-spec.json" with { type: "json" };
import { announceActionUrl, findActionUrl } from "./browser-links";
import { apiBaseUrl, validateApiKey } from "../auth/client";
import { getActiveProfile, getValidAccessToken } from "../auth/profiles";
import { WHOP_SCOPES } from "../auth/scopes";
import type { Profile } from "../auth/types";
import { requiredOverrides } from "./required-overrides";

const HTTP_METHODS = new Set([
	"get",
	"put",
	"post",
	"delete",
	"options",
	"head",
	"patch",
	"trace",
]);

export { nativeSpec, appsSpec };

/**
 * The API version the built CLI is pinned to. sync-spec.ts bakes it into
 * native-spec.json as info["x-api-version-date"] at build time (and fails the
 * build when it's missing); without the header the backend resolves requests
 * to the legacy base version, not the latest shapes the spec commands are
 * generated from.
 */
const API_VERSION_HEADER = "Api-Version-Date";
export const API_VERSION_DATE: string = nativeSpec.info["x-api-version-date"];

/**
 * Returns a filtered copy of the spec containing only paths whose operations
 * carry the given tag. Passed to incur's `{ fetch, openapi }` command to
 * auto-generate typed subcommands for one command group at a time.
 */
/**
 * Parameter names that are redundant in CLI help: `api-version-date` is an
 * internal versioning header the fetch proxy injects. Matched case-insensitively
 * (the spec names it `Api-Version-Date`) and through `$ref`s into
 * `components.parameters`. Auth credentials never appear here — the spec only
 * declares them as security schemes, which incur skips via
 * `openapiConfig: { security: false }` because the proxy injects the Bearer token.
 */
const HIDDEN_PARAMS = new Set(["api-version-date"]);

/**
 * Account-scoping params the fetch proxy auto-fills from the active profile.
 * The spec often marks these `required`, which would make incur demand them on
 * the command line before the proxy ever runs — so we demote them to optional
 * (still overridable by power users) and let the proxy inject the account.
 */
const AUTO_FILLED_PARAMS = new Set(["company_id", "account_id"]);

/** `$ref` strings (e.g. `#/components/parameters/ApiVersionDate`) that resolve to a hidden param. */
function hiddenParamRefs(spec: Record<string, unknown>): Set<string> {
	const refs = new Set<string>();
	const components = (spec.components as Record<string, unknown> | undefined)
		?.parameters;
	if (components && typeof components === "object") {
		for (const [key, def] of Object.entries(
			components as Record<string, unknown>,
		)) {
			const name = (def as Record<string, unknown> | null)?.name;
			if (typeof name === "string" && HIDDEN_PARAMS.has(name.toLowerCase())) {
				refs.add(`#/components/parameters/${key}`);
			}
		}
	}
	return refs;
}

function isHiddenParam(p: unknown, hiddenRefs: Set<string>): boolean {
	if (p == null || typeof p !== "object") return false;
	const rec = p as Record<string, unknown>;
	if (typeof rec.$ref === "string" && hiddenRefs.has(rec.$ref)) return true;
	return (
		typeof rec.name === "string" && HIDDEN_PARAMS.has(rec.name.toLowerCase())
	);
}

function transformParams(
	op: Record<string, unknown>,
	hiddenRefs: Set<string>,
): Record<string, unknown> {
	const params = op.parameters;
	if (!Array.isArray(params)) return op;
	const relaxed = params
		.filter((p: unknown) => !isHiddenParam(p, hiddenRefs))
		.map((p: unknown) => {
			if (
				p != null &&
				typeof p === "object" &&
				AUTO_FILLED_PARAMS.has(
					String((p as Record<string, unknown>).name ?? ""),
				)
			) {
				return { ...(p as Record<string, unknown>), required: false };
			}
			return p;
		});
	return { ...op, parameters: relaxed };
}

export function filterSpecByTag(
	spec: Record<string, unknown>,
	tag: string,
): Record<string, unknown> {
	const hiddenRefs = hiddenParamRefs(spec);
	const paths: Record<string, Record<string, unknown>> = {};
	for (const [path, methods] of Object.entries(
		spec.paths as Record<string, Record<string, unknown>>,
	)) {
		const filtered: Record<string, unknown> = {};
		const nonOpEntries: [string, unknown][] = [];
		for (const [method, op] of Object.entries(methods)) {
			if (!HTTP_METHODS.has(method)) {
				nonOpEntries.push([method, op]);
				continue;
			}
			const tags = (op as Record<string, unknown>).tags;
			if (Array.isArray(tags) && tags.includes(tag)) {
				filtered[method] = transformParams(
					op as Record<string, unknown>,
					hiddenRefs,
				);
			}
		}
		if (Object.keys(filtered).length > 0) {
			for (const [k, v] of nonOpEntries) filtered[k] = v;
			paths[path] = filtered;
		}
	}
	return { ...spec, paths };
}

/** Body/query field names that carry the active account. */
export const ACCOUNT_FIELDS = ["company_id", "account_id"];

interface SpecOperation {
	parameters?: Array<{ name?: string; in?: string }>;
	requestBody?: {
		content?: Record<
			string,
			{ schema?: { properties?: Record<string, unknown> } }
		>;
	};
}

/** Finds the spec operation for a concrete request path + method (templated segments are wildcards). */
function findOperation(method: string, pathname: string): SpecOperation | null {
	const want = pathname.split("/").filter(Boolean);
	const m = method.toLowerCase();
	for (const spec of [nativeSpec, appsSpec]) {
		const paths = (spec as { paths?: Record<string, Record<string, unknown>> })
			.paths;
		if (!paths) continue;
		for (const [template, ops] of Object.entries(paths)) {
			const segs = template.split("/").filter(Boolean);
			if (segs.length !== want.length) continue;
			const matches = segs.every(
				(seg, i) =>
					(seg.startsWith("{") && seg.endsWith("}")) || seg === want[i],
			);
			if (matches && ops[m]) return ops[m] as SpecOperation;
		}
	}
	return null;
}

function declaresQueryParam(op: SpecOperation, name: string): boolean {
	return (op.parameters ?? []).some((p) => p.name === name && p.in === "query");
}

function declaresBodyProp(op: SpecOperation, name: string): boolean {
	const props =
		op.requestBody?.content?.["application/json"]?.schema?.properties;
	return !!props && Object.prototype.hasOwnProperty.call(props, name);
}

/**
 * The Whop API nests its error message under `{ error: { message } }`, but incur
 * only reads a top-level `message`, so every failure collapses to "HTTP 400".
 * Lift the real message to the top level so spec commands report what's wrong.
 */
function extractApiMessage(payload: unknown): string | undefined {
	if (payload == null || typeof payload !== "object") return undefined;
	const p = payload as Record<string, unknown>;
	const err = p.error;
	if (
		err &&
		typeof err === "object" &&
		typeof (err as Record<string, unknown>).message === "string"
	) {
		return (err as Record<string, string>).message;
	}
	for (const value of [p.message, p.detail, err]) {
		if (typeof value === "string") return value;
	}
	return undefined;
}

let envAccountId: Promise<string | undefined> | null = null;

function normalizeApiKey(key: string): string {
	return key.replace(/^Bearer\s+/i, "").trim();
}

async function resolveAccountId(
	profile: Profile | null,
	apiKey: string,
): Promise<string | undefined> {
	if (profile?.accountId) return profile.accountId;

	const envKey = process.env.WHOP_API_KEY
		? normalizeApiKey(process.env.WHOP_API_KEY)
		: undefined;
	if (!profile && envKey && apiKey === envKey) {
		// Memoize so concurrent requests in one command share a single validation,
		// but never cache a miss: a transient failure or not-yet-valid key must be
		// re-checked on the next request rather than locking the process to "no
		// account". A rejection is swallowed to undefined so it stays non-fatal —
		// API commands then surface a clear error instead of the proxy throwing.
		envAccountId ??= validateApiKey(envKey, apiBaseUrl())
			.then((result) =>
				result.status === "valid" || result.status === "underscoped"
					? result.identity.id
					: undefined,
			)
			.catch(() => undefined);
		const resolved = await envAccountId;
		if (resolved === undefined) envAccountId = null;
		return resolved;
	}

	return undefined;
}

/**
 * Resolves the active account id for hand-written commands, using the same rules
 * as the fetch proxy: the active profile's account, or — when only WHOP_API_KEY
 * is set (agents / CI) — the account the env key belongs to. Lets commands that
 * build their own requests reach the account without a saved login profile.
 */
export async function resolveActiveAccountId(): Promise<string | undefined> {
	const profile = getActiveProfile();
	const rawKey = profile
		? await getValidAccessToken(profile)
		: (process.env.WHOP_API_KEY ?? null);
	if (!rawKey) return undefined;
	return resolveAccountId(profile, normalizeApiKey(rawKey));
}

/**
 * Returns a fetch handler that proxies requests to the Whop API. It:
 *  - injects the active profile's credential as a Bearer token,
 *  - pins every request to the baked API version via Api-Version-Date,
 *  - auto-fills the active account (company_id/account_id) on any operation that
 *    declares it but didn't receive it — user-scoped OAuth tokens require this,
 *  - surfaces the API's real error message instead of a bare "HTTP <status>".
 *
 * Passed to incur's `{ fetch, openapi }` command to drive spec subcommands.
 */
export function createWhopFetch(): (request: Request) => Promise<Response> {
	return async (request: Request): Promise<Response> => {
		const profile = getActiveProfile();
		const rawKey = profile
			? await getValidAccessToken(profile)
			: (process.env.WHOP_API_KEY ?? null);

		if (!rawKey) {
			const message = profile
				? "Stored credential not found. Run `whop login` again."
				: "Not logged in. Run `whop login` first.";
			return Response.json({ message }, { status: 401 });
		}

		const token = normalizeApiKey(rawKey);

		const incomingUrl = new URL(request.url);
		const op = findOperation(request.method, incomingUrl.pathname);
		const accountId = await resolveAccountId(profile, token);
		const method = request.method.toUpperCase();

		let body = request.body ? await request.text() : undefined;

		// The single place account scoping happens: auto-fill the active account on
		// any operation that declares it but didn't receive it. Never overwrites a
		// value the caller set.
		if (op && accountId) {
			if (method === "GET" || method === "DELETE" || method === "HEAD") {
				for (const name of ACCOUNT_FIELDS) {
					if (
						!incomingUrl.searchParams.has(name) &&
						declaresQueryParam(op, name)
					) {
						incomingUrl.searchParams.set(name, accountId);
					}
				}
			} else {
				const parsed = body ? safeParse(body) : {};
				if (parsed && typeof parsed === "object") {
					const rec = parsed as Record<string, unknown>;
					let changed = false;
					for (const name of ACCOUNT_FIELDS) {
						// The API infers company_id from a plan and rejects it when one is
						// given, so never inject company_id alongside a plan_id.
						if (name === "company_id" && rec.plan_id != null) continue;
						if (rec[name] == null && declaresBodyProp(op, name)) {
							rec[name] = accountId;
							changed = true;
						}
					}
					if (changed) body = JSON.stringify(parsed);
				}
			}
		}

		const overrides = requiredOverrides(
			method,
			incomingUrl.pathname,
			profile?.method === "oauth" ? "user" : "business",
		);
		if (overrides.length > 0) {
			const parsed = body ? safeParse(body) : {};
			const rec =
				parsed && typeof parsed === "object"
					? (parsed as Record<string, unknown>)
					: {};
			const violation = overrides.find(
				({ name }) => rec[name] == null || rec[name] === "",
			);
			if (violation) {
				return Response.json({ message: violation.error }, { status: 400 });
			}
		}

		const base = apiBaseUrl().replace(/\/$/, "");
		const target = `${base}${incomingUrl.pathname}${incomingUrl.search}`;

		const headers = new Headers(request.headers);
		headers.set("Authorization", `Bearer ${token}`);
		headers.set("Content-Type", "application/json");
		if (!headers.has(API_VERSION_HEADER)) {
			headers.set(API_VERSION_HEADER, API_VERSION_DATE);
		}

		const res = await fetch(target, { method: request.method, headers, body });
		if (res.ok) {
			// Some flows (social account OAuth, hosted KYC, hosted deposits) can
			// only be finished in a browser — surface that instead of silently
			// printing a URL in the payload. Only on mutations: list/get responses
			// echo the same fields without asking for anything.
			if (method === "POST" || method === "PATCH") {
				const text = await res.text();
				const actionUrl = findActionUrl(safeParse(text));
				if (actionUrl) await announceActionUrl(actionUrl);
				return new Response(text, { status: res.status, headers: res.headers });
			}
			return res;
		}

		// Lift the API's nested error message to the top level for incur.
		const text = await res.text();
		const payload = safeParse(text);
		const errorActionUrl = findActionUrl(
			(payload as Record<string, unknown> | null)?.error,
		);
		if (errorActionUrl) await announceActionUrl(errorActionUrl);
		let message = extractApiMessage(payload) ?? text ?? `HTTP ${res.status}`;
		if (res.status === 403) {
			const scope = /not authorized for the (\S+) scope/i.exec(message)?.[1];
			if (scope) {
				// A scope we request at login is grantable — the stored token just
				// predates it. A scope we don't request has no OAuth path at all.
				message += (WHOP_SCOPES as readonly string[]).includes(scope)
					? " Your login predates this scope — run `whop login` to re-authorize."
					: " This resource requires an API-key login (`whop login --api-key`).";
			}
		}
		message = withJourneyHint(message, payload);
		return Response.json(
			{ message, error: payload ?? text },
			{ status: res.status },
		);
	};
}

/**
 * Known dead-ends in multi-step journeys (ads launch, media generation) where
 * the API's message says what's wrong but not which CLI command fixes it.
 * Matched on the lifted message so the hints hold across endpoints that share
 * backend validations.
 */
const JOURNEY_HINTS: Array<{ pattern: RegExp; hint: string }> = [
	{
		pattern: /exactly one of ad_group_id or ad_group/i,
		hint: 'Pass --ad_group_id for an existing ad group, or create the whole tree in one call: whop ads create --title "..." --creatives \'[{"id":"file_x"}]\' --ad_group \'{"title":"...","conversion_location":"website","ad_campaign":{"title":"...","platform":"meta","objective":"sales","status":"draft","budget_amount":25,"budget_optimization":"ad_campaign"}}\'',
	},
	{
		pattern: /connect an ads payment method/i,
		hint: 'Configure billing with `whop accounts update-preferences <account_id> --ads_payment_methods \'{"primary":{"type":"platform_balance"}}\'`, funding the balance first via `whop deposits create` if needed.',
	},
	{
		pattern: /payment method requires user authentication/i,
		hint: "Run `whop login` (browser OAuth) — API-key credentials can only reorder already-configured payment methods.",
	},
];

function withJourneyHint(message: string, payload: unknown): string {
	const depositUrl =
		payload && typeof payload === "object"
			? ((payload as { error?: { deposit_url?: string } }).error?.deposit_url ??
				undefined)
			: undefined;
	if (depositUrl) return appendSentence(message, `Deposit at ${depositUrl}`);

	const hint = JOURNEY_HINTS.find(({ pattern }) => pattern.test(message))?.hint;
	return hint ? appendSentence(message, hint) : message;
}

function appendSentence(message: string, addition: string): string {
	const trimmed = message.trim();
	return `${trimmed}${/[.!?]$/.test(trimmed) ? "" : "."} ${addition}`;
}

function safeParse(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}
